'use client';

import {memo} from 'react';
import type {Map as MapboxMap} from 'mapbox-gl';
import useMapSelectedPlacesList from './useMapSelectedPlacesList';
import getReviewsByPlaceId from 'src/utils/getReviewsByPlaceId';
import {arrowRightUpLine, closeLine} from 'src/utils/icons';
import useMapSelectedPlace from './useMapSelectedPlace';
import {setSearchParam} from 'src/hooks/useSearchParam';
import ProgressCircle from '../common/ProgressCircle';
import SmallLoader from '../common/SmallLoader';
import MapPlacesList from './MapPlacesList';
import useMapUtils from './useMapUtils';
import BaseButton from '../BaseButton';
import RemixIcon from '../RemixIcon';
import Smooth from '../Smooth';

let wasFetching = false;

type Props = {
  reviews: Review[];
  map: MapboxMap | null;
};

const PlaceListModal = ({reviews, map}: Props) => {
  const {placesList, isFetching} = useMapSelectedPlacesList();
  const places = placesList?.places.map((placeEntry) => placeEntry.place);
  const {setSelectedPlace, unSelectPlace} = useMapSelectedPlace({places, map});

  const {zoomToPlaces} = useMapUtils(map);

  if (wasFetching && !isFetching && placesList?.places.length) {
    zoomToPlaces(placesList?.places.map((placeEntry) => placeEntry.place));
  }

  wasFetching = isFetching;

  const reviewsByPlaceId = getReviewsByPlaceId(reviews || []);
  const intersection = placesList?.places.filter((place) => reviewsByPlaceId[place.place?._id]).length || 0;

  return (
    <div className="absolute animate-show-up w-full max-[800px]:relative top-0 bottom-6 pr-6 max-[800px]:pr-4 z-3">
      <div className="relative w-full min-h-[140px] h-full rounded-2xl bg-(--button-bg) btn-border dark:shadow-[0_0_0_1px_rgb(245_225_255_/_0.12)]">
        {isFetching && !placesList ? (
          <div className="absolute w-full flex items-center justify-center h-full">
            <SmallLoader />
          </div>
        ) : null}

        {placesList ? (
          <Smooth className="overflow-y-auto h-full p-3 max-[800px]:p-2">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-4 items-start p-2 pb-0">
                <h2 className="pr-10 text-lg/snug font-semibold">{placesList.name}</h2>

                <div className="flex items-center gap-4">
                  {placesList.isListicle ? (
                    <BaseButton
                      buttonVariant="secondary"
                      size="small"
                      className="gap-1"
                      href={`/humbo/lists/${placesList.nameId}`}
                      target="_blank"
                    >
                      Read
                      <RemixIcon icon={arrowRightUpLine} color="var(--text-color)" size={18} className="-mr-1" />
                    </BaseButton>
                  ) : null}

                  <div className="flex text-(--green-primary) rounded-md gap-2 items-center">
                    <ProgressCircle
                      progress={(intersection / (placesList.places.length || 1)) * 100}
                      color="var(--green-primary)"
                    />
                    Visited {intersection} / {placesList.places.length}
                  </div>
                </div>
              </div>

              <MapPlacesList
                virtualScroll={false}
                numberOfItemsToRender={placesList.places.length}
                containerHeight={0}
                itemData={{
                  places: places || [],
                  reviewsByPlaceId,
                  setSelectedPlace
                }}
              />
            </div>
          </Smooth>
        ) : null}

        <BaseButton
          onPress={() => {
            unSelectPlace();
            setSearchParam('places-list', '');
          }}
          icon={closeLine}
          buttonVariant="translucent"
          className="w-[32px] h-[32px] absolute right-4 top-4"
        />
      </div>
    </div>
  );
};

export default memo(PlaceListModal);
